import { useEffect, useState } from "react";
import QRCodeStyling from "qr-code-styling";
import { useTranslation } from "react-i18next";
import { ClientError } from "../../../../../api/src/shared/request";
import { displayToast } from "../../../components/app/AppToast";
import BottomPanel from "../../../components/app/panels/BottomPanel";
import { Button } from "../../../components/ui/Button";
import { REDIRECT_HOST } from "../../../config";
import { env } from "../../../config/env";
import { apiClient } from "../../../services/api";
import { shareImage } from "../../app/utils/shareImage";
import { shareUrl } from "../../app/utils/shareUrl";
import { gaPushEvent } from "../../../tools/analytics/base.analytics";
import { navClickShareRoom } from "../../../tools/analytics/domains/nav.analytics";
import { isNativeApp } from "../../../common/hooks/useIsDesktopView";
import { getRoomName } from "./useRoomName";
import { useCurrentUser } from "../../user/hooks/useCurrentUser";
import { useActiveLang } from "../../app/hooks/useActiveLang";
import type { PrivateRoom } from "../privateRoom";

type ActiveShare = {
  inviteCode: string;
  qrCodeUrl: string;
  shareUrl: string;
  shareText: string;
};

export type UseShareRoom = {
  triggerShare: () => Promise<void>;
  /** Copies the room's link: its page when public, its invitation otherwise. */
  copyLink: () => Promise<void>;
  renderShareSheet: () => React.JSX.Element | null;
};

async function generateQrCodeUrl(inviteCode: string): Promise<string | null> {
  const url = `https://${REDIRECT_HOST}/${inviteCode}`;
  const qrCode = new QRCodeStyling({
    data: url,
    width: 512,
    height: 512,
    dotsOptions: { type: "dots" },
    image:
      env.appEnv === "production"
        ? "/juisci-logo-tube.png"
        : "/juisci-logo-tube-dev.png",
    imageOptions: {
      imageSize: 0.4,
      margin: 4,
      crossOrigin: "anonymous",
    },
  });
  const raw = await qrCode.getRawData("png");
  if (!(raw instanceof Blob)) return null;
  return URL.createObjectURL(raw);
}

export function useShareRoom(roomId: string, room: PrivateRoom): UseShareRoom {
  const { t } = useTranslation();
  // CONTENT language: every caller is a view/room/* page sharing the CURRENT room,
  // so the shared name follows the in-room translate control.
  const { roomTextLang } = useActiveLang();
  const { currentUser } = useCurrentUser();
  const roomName = room === null ? "" : getRoomName(room, roomTextLang);
  const [isOpen, setIsOpen] = useState(false);
  const [active, setActive] = useState<ActiveShare | null>(null);

  useEffect(() => {
    return () => {
      if (active !== null) URL.revokeObjectURL(active.qrCodeUrl);
    };
  }, [active]);

  const close = (): void => {
    setIsOpen(false);
  };

  // A public room is shared by its plain URL, which carried no sharer and so
  // credited no sign-up to anyone; `shared` is what every other user share
  // link carries (`tools/analytics/shareAttribution.ts`). A private room's
  // invite code is attributed server-side through `Invite.user`.
  const publicRoomUrl = `${globalThis.location.origin}/room/discovery/${roomId}${
    currentUser === null ? "" : `?shared=${currentUser.id}`
  }`;
  // A private room is only reachable through its invitation.
  function roomLink(inviteCode: string): string {
    return room.isPublic
      ? publicRoomUrl
      : `https://${REDIRECT_HOST}/${inviteCode}`;
  }

  const copyLink = async (): Promise<void> => {
    try {
      const url = room.isPublic
        ? publicRoomUrl
        : roomLink(
            (
              await apiClient.post("/webapp/private/rooms/:id/short-links", {
                param: { id: roomId },
              })
            ).body
          );
      await navigator.clipboard.writeText(url);
      displayToast(t("Link copied"), "success");
    } catch {
      displayToast(t("Something went wrong. Please try again."), "error");
    }
  };

  const triggerShare = async (): Promise<void> => {
    const genericError = t("Something went wrong. Please try again.");

    try {
      const { body: inviteCode } = await apiClient.post(
        "/webapp/private/rooms/:id/short-links",
        { param: { id: roomId } }
      );

      const qrCodeUrl = await generateQrCodeUrl(inviteCode);
      if (qrCodeUrl === null) {
        displayToast(genericError, "error");
        return;
      }

      const targetUrl = roomLink(inviteCode);
      const orgName = room.organisationModel.name;
      const shareText =
        orgName.trim().length === 0
          ? t(
              "{{roomName}} Room - curated by Juisci. Independent scientific selection: peer-reviewed publications, guidelines, and expert content for Healthcare Professionals.",
              { roomName }
            )
          : t(
              "{{roomName}} Room - curated by Juisci, supported by {{orgName}}. Independent scientific selection: peer-reviewed publications, guidelines, and expert content for Healthcare Professionals.",
              { roomName, orgName }
            );

      setActive({
        inviteCode,
        qrCodeUrl,
        shareUrl: targetUrl,
        shareText,
      });
      setIsOpen(true);
      gaPushEvent("room_qr_code_opened", {
        room_name: room.translations.en.name,
      });
    } catch (error) {
      const isNotFound =
        error instanceof ClientError && error.responseStatus === 404;
      displayToast(
        isNotFound
          ? t("QR sharing isn't available for your organization yet.")
          : genericError,
        "info"
      );
    }
  };

  const handleDownloadQrCode = async (): Promise<void> => {
    if (active === null) return;
    try {
      const response = await fetch(active.qrCodeUrl);
      const blob = await response.blob();
      await shareImage(
        blob,
        `${roomName === "" ? "room" : roomName}-qr-code.png`,
        t("QR code downloaded")
      );
      navClickShareRoom(room.translations.en.name, "downloaded_qr_code");
    } catch {
      displayToast(t("Something went wrong. Please try again."), "error");
    }
  };

  const handleShareRoom = async (): Promise<void> => {
    if (active === null) return;
    try {
      await shareUrl(
        roomName,
        active.shareText,
        Promise.resolve(active.shareUrl)
      );
      navClickShareRoom(room.translations.en.name, "link");
    } catch (error) {
      if (error instanceof DOMException && error.name === "AbortError") return;
      displayToast(t("Something went wrong. Please try again."), "error");
    }
  };

  const renderShareSheet = (): React.JSX.Element | null => {
    return (
      <BottomPanel
        isOpen={isOpen}
        onClose={close}
        height={isNativeApp() ? "83dvh" : "90dvh"}
        displayCloseButton
      >
        {active === null ? null : (
          <div className="flex flex-1 flex-col justify-between gap-8 px-5 pt-8 pb-8">
            <div className="flex flex-col gap-4">
              <img
                className="mx-auto w-5/7 max-w-[30dvh]"
                src={active.qrCodeUrl}
                alt={t("QR Code")}
              />
              <div className="text-2xl/tight font-bold text-center ">
                {t("Bring your peers into our exclusive community!")}
              </div>
              <div className="text-base/normal font-normal text-secondary-bluegrey-4  text-center">
                {t(
                  "Just have them scan this QR code to start connecting in this dedicated space."
                )}
              </div>
            </div>
            <div className="flex flex-col gap-3">
              <Button
                variant="outline"
                size="md"
                onClick={() => {
                  void handleDownloadQrCode();
                }}
              >
                {t("Download QR code")}
              </Button>
              <Button
                variant="primary"
                size="md"
                onClick={() => {
                  void handleShareRoom();
                }}
              >
                {t("Share Room")}
              </Button>
            </div>
          </div>
        )}
      </BottomPanel>
    );
  };

  return { triggerShare, copyLink, renderShareSheet };
}
